import { useId, useRef } from 'react';

export interface UseSelectTopOnEnterOptions<T extends object> {
  selectOnEnter: boolean | undefined;
  selectOnTab: boolean | undefined;
  isLoading: boolean;
  filteredOptions: T[];
  value?: T | null;
  getOptionLabel: (option: T) => string;
  inputValueRef: React.MutableRefObject<string>;
  queueCommit: (option: T) => void;
}

export interface SelectTopOnEnter {
  listboxId: string;
  handleKeyDownCapture: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function useSelectTopOnEnter<T extends object>({
  selectOnEnter,
  selectOnTab,
  isLoading,
  filteredOptions,
  value,
  getOptionLabel,
  inputValueRef,
  queueCommit,
}: UseSelectTopOnEnterOptions<T>): SelectTopOnEnter {
  const simulatingKeysRef = useRef(false);
  const listboxId = useId();

  /** The option Tab/Enter would take: whatever the user arrowed to, else the top result. */
  const getTargetOption = () => {
    const items = document.getElementById(listboxId)?.querySelectorAll('[role="option"]');
    const focusedIndex = items ? Array.from(items).findIndex((el) => el.hasAttribute('data-focused')) : -1;

    return filteredOptions[focusedIndex >= 0 ? focusedIndex : 0];
  };

  /** Shared entry conditions: the user typed something, results exist, and it isn't already committed. */
  const hasUncommittedMatch = () => {
    if (isLoading) return false;
    if (!inputValueRef.current.trim() || filteredOptions.length === 0) return false;
    if (value && getOptionLabel(value) === inputValueRef.current) return false;

    return true;
  };

  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (simulatingKeysRef.current) return;

    // Tab takes the highlighted match on the way out. Deliberately NOT prevented: the commit is a
    // side effect and Tab keeps moving focus to the next field, which is the whole point of
    // tabbing through a form. Shift+Tab is excluded — moving backwards is not an accept.
    // The option is QUEUED, not written here — see `commitBlur`, which applies it once focus has
    // actually left, so the listbox does not reopen behind the next field.
    if (selectOnTab && e.key === 'Tab' && !e.shiftKey && !e.nativeEvent.isComposing) {
      if (!hasUncommittedMatch()) return;
      const target = getTargetOption();
      if (target) queueCommit(target);

      return;
    }

    if (!selectOnEnter || e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    if (isLoading) return;
    if (!inputValueRef.current.trim() || filteredOptions.length === 0) return;
    if (value && getOptionLabel(value) === inputValueRef.current) return;
    if (document.getElementById(listboxId)?.querySelector('[data-focused]')) return;
    e.preventDefault();
    e.stopPropagation();
    const input = e.currentTarget;
    simulatingKeysRef.current = true;
    input.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
    input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
    simulatingKeysRef.current = false;
  };

  return { listboxId, handleKeyDownCapture };
}
